import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { AutoRenewalCheckbox, ExpiryTimeSelection } from '@steelbuy/ui-domain';
import { useWizardStepsContext, WizardBarItemStatus } from '@steelbuy/ui-primitive';
import { createRouteUrl, RoutePath } from '../../../router/Routes';
import { FormLayout } from '../../../views/layouts/form-layout/FormLayout';
import { WizardActionBar } from '../../wizard-action-bar/WizardActionBar';
import { useBulkListingContext } from '../bulk-listing-context/BulkListingContext';
import { BulkListingStep } from '../BulkListing.enums';
import { getStepRoute } from '../bulkListingUtil';

export const ExpiryDateStep = () => {
    const { draftId = '' } = useParams();
    const { state } = useLocation();
    const { t } = useTranslation(['uiDomain', 'translation']);
    const navigate = useNavigate();
    const { updateStepStatus, isStepVisited } = useWizardStepsContext();
    const { control, trigger, watch, getValues } = useFormContext();
    const [isValid, setIsValid] = useState(false);
    const { handleSaveDraft, updateBulkUploadMutation } = useBulkListingContext();
    const { listingsIncompleteCount, expireDate } = useOutletContext<{
        listingsIncompleteCount: number;
        expireDate: Date;
    }>();

    const stepVisited = isStepVisited(BulkListingStep.EXPIRY_DATE);

    useEffect(() => {
        if (listingsIncompleteCount === 0) {
            updateStepStatus(BulkListingStep.EDIT_LISTINGS, WizardBarItemStatus.SUCCESS);
        } else {
            updateStepStatus(BulkListingStep.EXPIRY_DATE, WizardBarItemStatus.EMPTY);
            navigate(getStepRoute(3, draftId), { replace: true });
        }
    }, [listingsIncompleteCount]);

    useEffect(() => {
        (async () => {
            const areExpiryFieldsValid = await trigger(['expireDate']);
            setIsValid(areExpiryFieldsValid);
        })();
    }, [watch('expireDate')]);

    if (!getValues('listings') || getValues('listings')?.length === 0) {
        return null;
    }

    return (
        <FormLayout>
            <p className="bulk-listing-import-listing__title">
                {t('translation:application.createBulkListing.expiryDate.header')}
            </p>
            <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                    <ExpiryTimeSelection
                        header={t('expiryTimeSelection.formItemHeader')}
                        label={t('expiryTimeSelection.label')}
                        expiryTime={value}
                        expiryTimeChanged={onChange}
                        required
                        forceValidation={stepVisited}
                    />
                )}
                name="expireDate"
                rules={{ required: true }}
            />
            <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                    <AutoRenewalCheckbox
                        enabled={value ?? false}
                        onChange={(val) => {
                            onChange(val);
                        }}
                    />
                )}
                name="autoRenew"
            />
            <WizardActionBar
                onNext={() => {
                    updateStepStatus(BulkListingStep.EXPIRY_DATE, WizardBarItemStatus.SUCCESS);
                    navigate(
                        draftId
                            ? createRouteUrl(RoutePath.CREATE_BULK_LISTING_SUMMARY_DRAFT, ['draftId', draftId])
                            : RoutePath.CREATE_BULK_LISTING_SUMMARY,
                        { state }
                    );
                }}
                isSubmitDisabled={!isValid}
                nextLabel={t('translation:application.createRFQ.wizardNextButtonLabel')}
                isLoading={updateBulkUploadMutation.isLoading}
                onBack={() => {
                    updateStepStatus(
                        BulkListingStep.EXPIRY_DATE,
                        expireDate ? WizardBarItemStatus.SUCCESS : WizardBarItemStatus.INCOMPLETE
                    );
                    if (state?.fromWizard) {
                        navigate(-1);
                    } else {
                        navigate(getStepRoute(3, draftId), { replace: true });
                    }
                }}
                onSaveDraft={() => handleSaveDraft(draftId)}
            />
        </FormLayout>
    );
};
